import { Col, Form, Row, Modal } from 'antd';
import { useEffect, useState, useMemo } from 'react';
import AnalysisChart from './chart';
import { useSelected } from '../../../../../providers/selectedContext';
import { updateSample } from '../../../../../api/data';
import { useAppContext } from '../../../../../providers/AppContext';
import { Accordion } from '../../../../../design-system/accordion';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { ColumnConfig, DataRow } from '../../../../../@types/data/types';
import DynamicDataTable from '../../../../forms/dynamicForm';
import FormWrapper from '../components/FormWrapper/FormWrapper';
import ConfirmClearModal from '../components/ConfirmClearModal/ConfirmClearModal';

const waterOilColumns: ColumnConfig[] = [
  { key: 'sw', header: 'Sw fraction' },
  { key: 'krw', header: 'Krw Lab' },
  { key: 'kro', header: 'Kro Lab' },
];

const AnalysisWaterOil = () => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { configStates, updateConfigStates, data, updateData } = useAppContext();
  const [tableData, setTableData] = useState<DataRow[]>([]);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  useEffect(() => {
    const inputCurves = selectedInfo?.sample?.input_sample_curves?.water_oil;
    if (inputCurves && inputCurves.sw_krel) {
      const loadedData = inputCurves.sw_krel.map((_, index) => ({
        sw: inputCurves?.sw_krel?.[index]?.toString() || '',
        krw: inputCurves?.krw_lab?.[index]?.toString() || '',
        kro: inputCurves?.kro_lab?.[index]?.toString() || '',
      }));
      setTableData([...loadedData, { sw: '', krw: '', kro: '' }]);
    } else {
      setTableData([{ sw: '', krw: '', kro: '' }]);
    }
  }, [selectedInfo]);

  const chartSeries = useMemo(() => {
    const validData = tableData.filter(row => row.sw && (row.krw || row.kro));

    const krwPoints = validData
      .map(row => [Number(row.sw), Number(row.krw)] as const)
      .filter(([sw, krw]) => !isNaN(sw) && !isNaN(krw));

    const kroPoints = validData
      .map(row => [Number(row.sw), Number(row.kro)] as const)
      .filter(([sw, kro]) => !isNaN(sw) && !isNaN(kro));

    return [
      {
        type: 'scatter',
        name: 'Krw Lab',
        data: krwPoints,
        color: '#3c85fa',
        lineWidth: 2
      },
      {
        type: 'scatter',
        name: 'Kro Lab',
        data: kroPoints,
        color: '#059e05',
        lineWidth: 2
      }
    ] as Highcharts.SeriesOptionsType[];;
  }, [tableData]);

  const handleAddRow = () => {
    const emptyRow = waterOilColumns.reduce((acc, col) => ({ ...acc, [col.key]: '' }), {});
    setTableData([...tableData, emptyRow]);
  };

  const handleRemoveRow = () => {
    if (tableData.length > 1) {
      setTableData(tableData.slice(0, -1));
    }
  };

  const handleClearAll = () => {
    setIsClearModalOpen(true);
  };

  const handleCancelClear = () => {
    setIsClearModalOpen(false);
  };

  const handleConfirmClear = () => {
    setTableData([{ sw: '', krw: '', kro: '' }]);
    setIsClearModalOpen(false);
  };

  const hasDataToClear =
    tableData.length > 1 ||
    (tableData.length === 1 && Object.values(tableData[0] || {}).some(cell => cell !== ''));

  const handleSubmit = async () => {
    if (!selectedInfo?.sample) return;

    updateConfigStates({ loadingKrAnalysis: true });

    const dataToSubmit = tableData.filter(row =>
      Object.values(row).some(cell => cell !== '')
    );

    const payload = {
      input_sample_curves: {
        water_oil: {
          sw_krel: dataToSubmit.map(row => Number(row.sw) || 0),
          krw_lab: dataToSubmit.map(row => Number(row.krw) || 0),
          kro_lab: dataToSubmit.map(row => Number(row.kro) || 0),
          fw_lab: null,
        },
        gas_oil: null,
        pc_model: null,
      },
      sample_uid: selectedInfo.sample.uid,
    };

    try {
      const response = await updateSample(payload);
      const newWellSamplesData = data.wellSamples.map((sample) => {
        if (sample.uid === response.uid) {
          return response
        }

        return sample
      })
      updateData({ wellSamples: newWellSamplesData })
      setSelectedInfo({ sample: response });
    } catch (error) {
      console.error('error', error);
    }

    updateConfigStates({ loadingKrAnalysis: false });
  };

  return (
    <>
      <Accordion defaultOpen="lab-data-water-oil">
        <Accordion.Item
          id="lab-data-water-oil"
          header={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
              <span>Laboratory Data</span>
              <span style={{ color: colors.waterOil }}>Water-Oil</span>
            </div>
          }
        >
          <div style={{ padding: '0.5rem' }}>
            <Row>
              <Col span={12}>
                <Form onFinish={handleSubmit}>
                  <FormWrapper
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    onClearAll={handleClearAll}
                    showClearButton={hasDataToClear}
                    isSubmitting={configStates.loadingKrAnalysis}
                  >
                    <DynamicDataTable
                      columns={waterOilColumns}
                      data={tableData}
                      onDataChange={setTableData}
                    />
                  </FormWrapper>
                </Form>
              </Col>
              <Col span={12}>
                <AnalysisChart data={chartSeries} />
              </Col>
            </Row>
          </div>
        </Accordion.Item>
      </Accordion>
      <ConfirmClearModal
        isOpen={isClearModalOpen}
        onClose={handleCancelClear}
        onConfirm={handleConfirmClear}
      />
    </>
  );
};

export default AnalysisWaterOil;
